/**
 * Service Worker Helper
 * Utility untuk menangani registrasi dan error handling ServiceWorker
 */

export const isServiceWorkerSupported = () => {
  return "serviceWorker" in navigator;
};

export const unregisterAllServiceWorkers = async () => {
  if (!isServiceWorkerSupported()) {
    console.log("Service Worker tidak didukung di browser ini");
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (let registration of registrations) {
      await registration.unregister();
      console.log("Service Worker unregistered:", registration.scope);
    }
  } catch (error) {
    console.error("Error unregistering service workers:", error);
  }
};

export const registerServiceWorker = async (scriptURL, options = {}) => {
  if (!isServiceWorkerSupported()) {
    console.log("Service Worker tidak didukung di browser ini");
    return null;
  }

  try {
    // Default options
    const defaultOptions = {
      scope: "/",
      updateViaCache: "none",
    };

    const finalOptions = { ...defaultOptions, ...options };

    // Register service worker
    const registration = await navigator.serviceWorker.register(scriptURL, finalOptions);
    console.log("✅ Service Worker registered successfully:", registration.scope);

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    console.log("✅ Service Worker is ready");

    return registration;
  } catch (error) {
    console.error("❌ Service Worker registration failed:", error);

    // Detailed error logging
    if (error.name === "SecurityError") {
      console.error("Security Error: Service Worker harus dijalankan melalui HTTPS atau localhost");
    } else if (error.name === "TypeError") {
      console.error("Type Error: File Service Worker tidak ditemukan atau tidak valid");
    } else if (error.message?.includes("script evaluation failed")) {
      console.error("Script Evaluation Error: Ada error dalam kode Service Worker");
      console.error("Periksa file:", scriptURL);
      console.error("Pastikan semua importScripts berhasil dimuat");
    }

    throw error;
  }
};

export const checkServiceWorkerStatus = async () => {
  if (!isServiceWorkerSupported()) {
    return {
      supported: false,
      registered: false,
      active: false,
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return {
      supported: true,
      registered: !!registration,
      active: !!registration?.active,
      scope: registration?.scope,
      scriptURL: registration?.active?.scriptURL,
    };
  } catch (error) {
    console.error("Error checking service worker status:", error);
    return {
      supported: true,
      registered: false,
      active: false,
      error: error.message,
    };
  }
};

export const waitForServiceWorkerActivation = async (registration, timeout = 10000) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Service Worker activation timeout"));
    }, timeout);

    if (registration.active) {
      clearTimeout(timeoutId);
      resolve(registration);
      return;
    }

    const serviceWorker = registration.installing || registration.waiting;
    if (!serviceWorker) {
      clearTimeout(timeoutId);
      reject(new Error("No service worker found"));
      return;
    }

    serviceWorker.addEventListener("statechange", function onStateChange() {
      if (this.state === "activated") {
        clearTimeout(timeoutId);
        serviceWorker.removeEventListener("statechange", onStateChange);
        resolve(registration);
      } else if (this.state === "redundant") {
        clearTimeout(timeoutId);
        serviceWorker.removeEventListener("statechange", onStateChange);
        reject(new Error("Service Worker became redundant"));
      }
    });
  });
};
